import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Sun, Moon, Globe } from "lucide-react";
import { NAV_LINKS, SITE } from "../data/mock";
import { useTheme } from "../context/ThemeContext";
import { useLang } from "../context/LanguageContext";

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const { theme, toggle } = useTheme();
  const { lang, toggle: toggleLang, t } = useLang();

  const LangSwitch = ({ className = "" }) => (
    <button
      onClick={toggleLang}
      aria-label="Toggle language"
      title={lang === "en" ? "Cambiar a Espa\u00f1ol" : "Switch to English"}
      className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border border-white/25 text-xs font-semibold text-white hover:bg-white/10 transition-colors ${className}`}
    >
      <Globe size={14} className="opacity-80" />
      <span className={lang === "en" ? "text-white" : "text-white/50"}>EN</span>
      <span className="text-white/40">|</span>
      <span className={lang === "es" ? "text-white" : "text-white/50"}>ES</span>
    </button>
  );

  return (
    <header className="sticky top-0 z-50 bg-[#800000] dark:bg-black shadow-md border-b-2 border-[#14532d] transition-colors">
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <Link to="/" className="flex items-center gap-3 group">
            <div className="relative">
              <img
                src={SITE.logoUrl}
                alt="Yoshitaka Karate-Do logo"
                className="h-16 w-16 sm:h-20 sm:w-20 object-contain drop-shadow-lg group-hover:scale-105 transition-transform -mb-4"
                style={{ filter: "drop-shadow(0 4px 6px rgba(0,0,0,0.4))" }}
              />
            </div>
            <div className="hidden sm:flex flex-col leading-tight">
              <span className="text-white font-bold text-base sm:text-lg tracking-wide">
                YOSHITAKA
              </span>
              <span className="text-emerald-300 text-[10px] sm:text-xs tracking-[0.25em] uppercase font-semibold">
                Karate-Do
              </span>
            </div>
          </Link>

          {/* Desktop nav */}
          <ul className="hidden md:flex items-center gap-6">
            {NAV_LINKS.map((l) => {
              const active = location.pathname === l.to;
              const label = t(`nav.${l.key}`);
              return (
                <li key={l.to}>
                  <Link
                    to={l.to}
                    className={`text-sm font-medium tracking-wide uppercase transition-colors duration-200 ${
                      active
                        ? "text-white border-b-2 border-emerald-300 pb-1"
                        : "text-white/90 hover:text-emerald-300"
                    }`}
                  >
                    {label}
                  </Link>
                </li>
              );
            })}
            <li>
              <LangSwitch />
            </li>
            <li>
              <button
                onClick={toggle}
                aria-label="Toggle dark mode"
                className="p-2 rounded-md text-white hover:bg-white/10 transition-colors"
              >
                {theme === "light" ? <Moon size={20} /> : <Sun size={20} />}
              </button>
            </li>
            <li>
              <a
                href={`tel:${SITE.phone.replace(/[^0-9]/g, "")}`}
                className="text-sm font-semibold text-[#800000] bg-white hover:bg-emerald-50 px-4 py-2 rounded-md transition-colors shadow"
              >
                {SITE.phone}
              </a>
            </li>
          </ul>

          {/* Mobile toggle */}
          <div className="md:hidden flex items-center gap-1">
            <LangSwitch />
            <button
              onClick={toggle}
              aria-label="Toggle dark mode"
              className="p-2 rounded-md text-white hover:bg-white/10"
            >
              {theme === "light" ? <Moon size={20} /> : <Sun size={20} />}
            </button>
            <button
              className="text-white p-2"
              onClick={() => setOpen(!open)}
              aria-label="Menu"
            >
              {open ? <X size={26} /> : <Menu size={26} />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {open && (
          <div className="md:hidden pb-4 border-t border-white/20">
            <ul className="flex flex-col gap-1 pt-3">
              {NAV_LINKS.map((l) => (
                <li key={l.to}>
                  <Link
                    to={l.to}
                    onClick={() => setOpen(false)}
                    className={`block px-3 py-2 rounded font-medium uppercase text-sm tracking-wide ${
                      location.pathname === l.to
                        ? "bg-white/10 text-white"
                        : "text-white/90 hover:text-emerald-300 hover:bg-white/5"
                    }`}
                  >
                    {t(`nav.${l.key}`)}
                  </Link>
                </li>
              ))}
              <li>
                <a
                  href={`tel:${SITE.phone.replace(/[^0-9]/g, "")}`}
                  className="block mt-2 px-4 py-2 text-center text-[#800000] bg-white rounded-md font-semibold"
                >
                  {SITE.phone}
                </a>
              </li>
            </ul>
          </div>
        )}
      </nav>
    </header>
  );
};

export default Navbar;
